/**
 * Gestion abstraite pour un store chargeant les templates de chartes graphiques
 * On utilise des stores différents dans le cas où on souhaite avoir un store unique en fonction du type de template
 */

import { ref, computed } from 'vue';
/***/
import axios from '@js/tools/axios';
/***/
import { useManagingUser } from '@compositions/ManagingUser';

/**
 * @param object : {
 *      templateType: <string>, // Type de template
 * }
 * @return object
 */
export function useAbstractGraphicalLineTemplateStore({
    templateType = null
}) {

    // Composition pour l'utilisateur que l'on gère
    const { getManagingUser } = useManagingUser({});

    // State de la liste des templates (total, templates)
    const templateList = ref({});

    // State pour différencier les données de la liste des templates
    const templates = ref([]);

    // State d'enregistrement de l'état des données à un instant t
    const restoreState = ref(null);

    /**
     * Retourne la liste des templates
     * @return object
     */
    const getTemplateList = computed(() => templateList.value || {});

    /**
     * Retourne le template dont l'identifiant est en paramètre
     * @param int templateId
     * @return object|null
     */
    const getTemplateById = computed(() => {
        return ((templateId) => {
            const index = templates.value.findIndex((templateFromState) => templateFromState.id === templateId);
            if(index === -1) {
                return null;
            }
            return templates.value[index];
        });
    });

    /**
     * Modifie la liste des templates
     * @param Array newList
     * @return void
     */
    const setTemplateList = ((newList) => {
         if(templateList.value.total === undefined) {
             return;
         }
        templateList.value.templates = [ ...newList ];
    });

    /**
     * Met à jour l'état de restauration
     * @return void
     */
    const refreshRestorationState = (() => {

        const canRefresh = (templateList.value.total !== undefined || templates.value.length > 0);

        if(! canRefresh) {
            return;
        }

        restoreState.value = {
            templateList: Object.assign({}, JSON.parse(JSON.stringify(templateList.value))),
            templates: [ ...templates.value ]
        };

    });

    /**
     * Restaure l'état enregistré précédemment
     * @return void
     */
    const restorePreviousState = (() => {

        if(restoreState.value === null) {
            return;
        }

        templateList.value = restoreState.value.templateList;
        templates.value = restoreState.value.templates;

    });

    /**
     * Chargement de la liste des templates
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadTemplateList = ((params) => {

        const path = `/api/users/${ getManagingUser.value.id }/graphical-line-templates`;

        if(templateType !== null) {
            params.type = templateType;
        }

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetTemplateList();
                }

                updateTemplateList(response.data.data);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Duplication du template en paramètre
     * @param object template
     * @param bool updateStore Vrai s'il faut mettre à jour le store après la duplication du template
     * @return Promise
     */
    const duplicateTemplate = ((template, updateStore) => {

        const path = `/api/users/${ getManagingUser.value.id }/graphical-line-templates/${ template.id }/duplicate`;

        return axios.post(path).then((response) => {
            if(response && response.data && response.data.success && response.data.newGraphicalLineTemplate) {
                if(updateStore) {
                    updateTemplate(response.data.newGraphicalLineTemplate);
                }
                return Promise.resolve(response.data.newGraphicalLineTemplate);
            }
            return Promise.reject();
        });
    });

    /**
     * Suppression du template en paramètre
     * @param object template
     * @return Promise
     */
    const removeTemplate = ((template) => {

        const path = `/api/users/${ getManagingUser.value.id }/graphical-line-templates/${ template.id }`;

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {
                onRemoveTemplate(template);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Supprime le template du store
     * @param object template Le template à supprimer
     * @return void
     */
    const onRemoveTemplate = ((template) => {

        // Suppression des templates déjà chargés
        const indexFromLoading = templates.value.findIndex((current) => current.id === template.id);
        if(indexFromLoading !== -1) {
            templates.value.splice(indexFromLoading, 1);
        }

        // Vérifie que la liste a été chargée
        const currentDataList = getTemplateList.value;
        if(currentDataList.total === undefined) {
            return;
        }

        // Suppression de la liste
        const newTemplateList = currentDataList.templates;
        const indexFromList = newTemplateList.findIndex((current) => current.id === template.id);
        if(indexFromList !== -1) {
            newTemplateList.splice(indexFromList, 1);
            templateList.value = {
                total: templateList.value.total - 1,
                templates: newTemplateList
            };
        }

    });

    /**
     * Modifie un template dans le store
     * @param object template Données du template à mettre à jour dans la liste
     * @param boolean canUpdateTotal Vrai si le nombre total d'éléments peut être mise à jour
     * @return Promise
     */
    const updateTemplate = ((template, canUpdateTotal = true) => {

        const currentTemplates = templates.value;

        const index = currentTemplates.findIndex((templateFromState) => templateFromState.id === template.id);
        if(index !== -1) {
            templates.value[index] = { ...templates.value[index], ...template };
        } else {
            templates.value.push(template);
        }

        // Synchronisation avec les éléments de la liste
        const listData = templateList.value;
        if(listData.total !== undefined) {
            const currentList = listData.templates || [];
            const indexInList = currentList.findIndex((templateFromState) => templateFromState.id === template.id);
            if(indexInList !== -1) {
                currentList[indexInList] = { ...currentList[indexInList], ...template };
            } else {
                if(canUpdateTotal) {
                    templateList.value.total += 1;
                }
                currentList.push(template);
            }

            templateList.value.templates = [ ...currentList ];
        }

    });

    /**
     * Modifie la liste des templates dans le store
     * @param data Données des templates : total et items
     * @return void
     */
    const updateTemplateList = ((data) => {

        const currentData = templateList.value;
        if(currentData.total === undefined) {
            templateList.value = {
                total: data.total,
                templates: data.items
            };

            data.items.forEach((template) => {
                updateTemplate(template);
            });

        } else {
            templateList.value.total = data.total;

            const currentTemplates = templateList.value.templates;
            const newTemplates = data.items.filter((item) => ! currentTemplates.find((current) => current.id === item.id));
            templateList.value.templates = [ ...currentTemplates, ...newTemplates ];

            newTemplates.forEach((template) => {
                updateTemplate(template);
            });
        }

    });

    /**
     * Réinitialisation de la liste des templates
     * @return void
     */
    const resetTemplateList = (() => {
        templateList.value = {};
    });

    /**
     * Réinitialisation de l'état de restauration
     * @return void
     */
    const resetRestorationState = (() => {
        restoreState.value = null;
    });

    return {
        getTemplateList,
        getTemplateById,
        loadTemplateList,
        updateTemplate,
        removeTemplate,
        duplicateTemplate,
        resetTemplateList,
        refreshRestorationState,
        restorePreviousState,
        resetRestorationState,
        setTemplateList
    };

};
